import { FC, useState } from 'react';
import { AdminLayout } from '@/components/admin/AdminLayout';
import { Wallet, Plus, TrendingUp, History, Settings } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { ExpenseDialog } from '@/components/admin/finance/ExpenseDialog';
import { ExpenseCard } from '@/components/admin/finance/ExpenseCard';
import { ExpenseStats } from '@/components/admin/finance/ExpenseStats';
import { FinancialSummary } from '@/components/admin/finance/FinancialSummary';
import { toast } from "sonner";

interface FinancialSummaryData {
  month: string;
  income: number;
  expenses: number;
  net_income: number;
}

const AdminFinance: FC = () => {
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  const { data: expenses, isLoading: expensesLoading } = useQuery({
    queryKey: ['platform-expenses'],
    queryFn: async () => {
      console.log('Fetching platform expenses...');
      const { data, error } = await supabase
        .from('platform_expenses')
        .select('*')
        .order('due_date', { ascending: true });

      if (error) {
        console.error('Error fetching expenses:', error);
        toast.error("No se pudieron cargar los gastos");
        throw error;
      }

      console.log('Expenses fetched:', data);
      return data;
    },
  });

  const { data: financialSummary, isLoading: summaryLoading } = useQuery({
    queryKey: ['financial-summary'],
    queryFn: async () => {
      console.log('Fetching financial summary...');
      const { data, error } = await supabase
        .rpc('get_financial_summary');

      if (error) {
        console.error('Error fetching financial summary:', error);
        toast.error("No se pudo cargar el resumen financiero");
        throw error;
      }

      console.log('Financial summary fetched:', data);
      return data as FinancialSummaryData[];
    },
  });

  // Calculate total historical summary
  const historicalSummary = financialSummary?.reduce((acc, curr) => ({
    month: 'all-time',
    income: acc.income + curr.income,
    expenses: acc.expenses + curr.expenses,
    net_income: acc.net_income + curr.net_income
  }), { month: 'all-time', income: 0, expenses: 0, net_income: 0 });

  return (
    <AdminLayout>
      <div className="container mx-auto px-4 py-8">
        <div className="flex items-center mb-8">
          <div className="flex items-center gap-3">
            <Wallet className="w-8 h-8 text-primary" />
            <h1 className="text-3xl font-bold text-gray-900">Finanzas Plataforma</h1>
          </div>
        </div>

        <div className="flex justify-end mb-6">
          <Button onClick={() => setIsDialogOpen(true)}>
            <Plus className="h-4 w-4 mr-2" />
            Agregar Gasto
          </Button>
        </div>

        {/* Historical Summary Section */}
        <div className="mb-8">
          <div className="flex items-center gap-2 mb-4">
            <History className="h-5 w-5 text-primary" />
            <h2 className="text-xl font-semibold">Resumen Histórico</h2>
          </div>
          {!summaryLoading && historicalSummary && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="bg-white rounded-lg shadow p-4">
                <p className="text-sm text-gray-500">Ingresos Totales</p>
                <p className="text-2xl font-bold text-green-600">
                  ${historicalSummary.income.toFixed(2)}
                </p>
              </div>
              <div className="bg-white rounded-lg shadow p-4">
                <p className="text-sm text-gray-500">Gastos Totales</p>
                <p className="text-2xl font-bold text-red-600">
                  ${historicalSummary.expenses.toFixed(2)}
                </p>
              </div>
              <div className="bg-white rounded-lg shadow p-4">
                <p className="text-sm text-gray-500">Balance Total</p>
                <p className={`text-2xl font-bold ${historicalSummary.net_income >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                  ${historicalSummary.net_income.toFixed(2)}
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Monthly Summary Section */}
        {!summaryLoading && financialSummary && (
          <FinancialSummary data={financialSummary} />
        )}

        {/* Expenses Section */}
        <div className="mt-8">
          <h2 className="text-xl font-semibold mb-4">Gastos Registrados</h2>
          {expenses && <ExpenseStats expenses={expenses} />}
          
          {expensesLoading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {[...Array(3)].map((_, i) => (
                <div key={i} className="h-32 bg-gray-100 animate-pulse rounded-lg" />
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {expenses?.map((expense) => (
                <ExpenseCard key={expense.id} expense={expense} />
              ))}
            </div>
          )}
        </div>

        <ExpenseDialog 
          open={isDialogOpen} 
          onOpenChange={setIsDialogOpen}
        />
      </div>
    </AdminLayout>
  );
};

export default AdminFinance;